import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';
import type { ActivityEntry } from '../components/ActivityCell';

export interface OfferVariant {
  id: string;
  offer_id: string;
  name: string;
  url: string;
  weight: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Offer {
  activity_status?: ActivityEntry | null;
  id: string;
  seq?: number;
  ref?: string;
  workspace_id: string;
  tag_ids?: string[];
  network_id: string | null;
  name: string;
  notes?: string | null;
  url: string;
  currency: string;
  report_to_source: boolean;
  geo: string | null;
  traffic_cap_limit: number;
  referrer_policy?: 'none' | 'origin' | 'full' | 'inherit' | null;
  use_redirect_page?: boolean | null;
  status: string;
  created_at: string;
  updated_at: string;
  postback_id: string | null;
  network?: { id: string; name: string } | null;
  postback?: { id: string; name: string } | null;
  variants?: OfferVariant[];
}

export const offersService = {
  query: async (filters: { limit?: number; offset?: number; network_id?: string; search?: string; where?: WhereCondition ; order_by?: string; order?: 'asc' | 'desc' } = {}): Promise<{ data: Offer[]; total: number }> => {
    const conds: WhereCondition[] = [];
    if (filters.network_id) conds.push({ offer: { network_id: filters.network_id } });
    if (filters.search) conds.push({ offer: { name: { op: 'ilike', value: `%${filters.search}%` } } });
    const where = filters.where ?? (conds.length === 0 ? undefined : conds.length === 1 ? conds[0] : { $and: conds });
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: filters.order_by ?? 'offer.created_at',
      order: filters.order ?? 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<Offer>>('/offers/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<Offer> => {
    const response = await api.get<ApiResponse<Offer>>(`/offers/${id}`);
    return response.data.data!;
  },

  create: async (data: Partial<Offer>): Promise<Offer> => {
    const response = await api.post<ApiResponse<Offer>>('/offers', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<Offer>): Promise<Offer> => {
    const response = await api.patch<ApiResponse<Offer>>(`/offers/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/offers/${id}`);
  },

  unarchive: async (id: string): Promise<Offer> => {
    const response = await api.patch<ApiResponse<Offer>>(`/offers/${id}/unarchive`);
    return response.data.data!;
  },

  // Variants
  createVariant: async (offerId: string, data: Partial<OfferVariant>): Promise<OfferVariant> => {
    const response = await api.post<ApiResponse<OfferVariant>>(`/offers/${offerId}/variants`, data);
    return response.data.data!;
  },

  updateVariant: async (offerId: string, variantId: string, data: Partial<OfferVariant>): Promise<OfferVariant> => {
    const response = await api.patch<ApiResponse<OfferVariant>>(`/offers/${offerId}/variants/${variantId}`, data);
    return response.data.data!;
  },

  deleteVariant: async (offerId: string, variantId: string): Promise<void> => {
    await api.delete(`/offers/${offerId}/variants/${variantId}`);
  },

};
